import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'cookie_consent_v1';
const CONSENT_EVENT = 'cookie_consent_changed';

/**
 * POPIA-aware cookie consent banner.
 *  - Shows once until a decision is recorded in localStorage.
 *  - Two clear choices: Accept all (analytics + marketing) / Reject non-essential.
 *  - Strictly-necessary cookies are not optional (sign-in, session, security).
 *  - Persists `{ analytics, marketing, necessary, decided_at }`.
 *  - Exposes `window.__openCookiePreferences()` so the footer can re-open it.
 *  - Broadcasts a `cookie_consent_changed` window event so any listener
 *    (marketing tags, etc.) can react without a page refresh.
 */
const CookieConsent = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_) {
      stored = null;
    }
    if (!stored || !stored.decided_at) setOpen(true);

    // Allow footer to reopen banner programmatically
    window.__openCookiePreferences = () => setOpen(true);
    return () => {
      try { delete window.__openCookiePreferences; } catch (_) { /* noop */ }
    };
  }, []);

  const persist = (choice) => {
    const payload = {
      necessary: true,
      analytics: !!choice.analytics,
      marketing: !!choice.marketing,
      decided_at: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) {
      // localStorage might be blocked in private mode — silently fail
    }
    try {
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: payload }));
    } catch (_) { /* older browsers */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:bottom-6 sm:right-auto sm:max-w-md z-[60]"
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="bg-white border border-gray-200 rounded-2xl shadow-2xl p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 bg-[#37b34a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Cookie size={18} className="text-[#37b34a]" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-gray-900">We value your privacy</h3>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              We use strictly necessary cookies to make the site work, and optional analytics &amp; marketing cookies to
              improve your experience. You can change your choice anytime. Read our{' '}
              <Link to="/privacy" className="text-[#37b34a] hover:underline" data-testid="cookie-privacy-link">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <button
            onClick={() => persist({ analytics: false, marketing: false })}
            className="p-1 text-gray-400 hover:text-gray-700 transition-colors -mr-1 -mt-1"
            aria-label="Close cookie banner"
            data-testid="cookie-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => persist({ analytics: false, marketing: false })}
            data-testid="cookie-reject"
            className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Reject non-essential
          </button>
          <button
            onClick={() => persist({ analytics: true, marketing: true })}
            data-testid="cookie-accept"
            className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold text-white bg-[#37b34a] hover:bg-[#2d9a3e] transition-colors"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
